import { useState, useCallback, useEffect, useRef } from 'react'
import { extractSectionsFromMarkdown } from '@/utils/documentUtils'

interface NodeDataSettings {
  inputLinks: any[]
  outputLinks: any[]
  // Remove availableNodes from persistent storage - compute dynamically instead
  documentSections?: {
    fullDocument: { value: string; enabled: boolean; lastUpdated: number }
    sections: Record<string, { value: string; enabled: boolean; lastUpdated: number }>
    enabled: boolean
  }
  exposedData?: any
  autoSync?: boolean
}

interface DocumentNodeData {
  dataSettings?: NodeDataSettings
  inputdata?: any
  linkedKnowledge?: string
  label?: string
}

interface Section {
  id: string
  title: string
  content: string
}

const useDataSettings = (
  id: string,
  data: DocumentNodeData,
  syncNodeData: (newData: Partial<DocumentNodeData>) => void,
  showDebugInfo: boolean,
  context: string,
  sections: Map<string, Section>,
  contentMarkdown: string,
  setIsDataSettingsDialogVisible: (visible: boolean) => void
) => {
  // Fix: Don't initialize with empty defaults that could overwrite collaborative state
  const [dataSettings, setDataSettings] = useState<NodeDataSettings>(() => ({
    inputLinks: [],
    outputLinks: [],
    // Remove availableNodes initialization
    ...data.dataSettings, // Use data.dataSettings directly, not as fallback
  }))

  const [sectionEnabledStates, setSectionEnabledStates] = useState<{
    fullDocument: boolean
    sections: Record<string, boolean>
  }>(() => {
    const initial = {
      fullDocument: data.dataSettings?.documentSections?.fullDocument?.enabled ?? true,
      sections: {},
    }
    if (data.dataSettings?.documentSections?.sections) {
      Object.entries(data.dataSettings.documentSections.sections).forEach(([title, section]) => {
        initial.sections[title] = section.enabled ?? true
      })
    }
    return initial
  })

  const prevInputLinksRef = useRef<any[]>([])
  const prevContentMarkdownRef = useRef<string>(contentMarkdown)

  const generateLinkedKnowledgeText = useCallback((settings = dataSettings) => {
    if (!settings?.inputLinks?.length) {
      if (data.linkedKnowledge !== '') {
        syncNodeData({ linkedKnowledge: '' })
      }
      return ''
    }
    const linkedDataLines: string[] = []
    settings.inputLinks.filter((link) => link.enabled).forEach((link) => {
      linkedDataLines.push(`## Data from: ${link.nodeName || link.nodeId}\n`)
      if (link.data) {
        Object.entries(link.data).filter(([_, data]) => data?.enabled).forEach(([key, data]) => {
          linkedDataLines.push(`${key}: ${data.value || ''}\n`)
        })
      }
      linkedDataLines.push('\n')
    })
    const linkedKnowledgeText = linkedDataLines.join('\n')
    // Only sync if not already up-to-date
    if (linkedKnowledgeText !== data.linkedKnowledge) {
      // Do not call syncNodeData here, just return the value
      // syncNodeData({ linkedKnowledge: linkedKnowledgeText })
    }
    return linkedKnowledgeText
  }, [data.linkedKnowledge])

  const prepareSectionsForDataSettings = useCallback(() => {
    const existingSections = data.dataSettings?.documentSections?.sections || {}
    const sectionFields: Record<string, any> = {}
    sections.forEach((section) => {
      const existingSection = existingSections[section.title]
      sectionFields[section.title] = {
        value: section.content,
        enabled: sectionEnabledStates.sections[section.title] ?? existingSection?.enabled ?? true,
        lastUpdated: existingSection?.lastUpdated ?? Date.now(),
      }
    })
    const existingFullDocument = data.dataSettings?.documentSections?.fullDocument
    return {
      fullDocument: {
        value: contentMarkdown,
        enabled: sectionEnabledStates.fullDocument ?? existingFullDocument?.enabled ?? true,
        lastUpdated: existingFullDocument?.lastUpdated ?? Date.now(),
      },
      sections: sectionFields,
      enabled: data.dataSettings?.documentSections?.enabled ?? true,
    }
  }, [sections, contentMarkdown, sectionEnabledStates, data.dataSettings])

  const prepareExposedDataFields = useCallback(() => {
    return {
      context: {
        value: context,
        enabled: true,
        label: 'Document Context',
        description: 'The context information for this document',
        permissions: { readable: true, writable: false },
      },
      documentTitle: {
        value: data.label || 'Untitled Document',
        enabled: true,
        label: 'Document Title',
        description: 'The title of this document',
        permissions: { readable: true, writable: false },
      },
    }
  }, [context, data.label])

  const fetchAvailableDocumentNodes = useCallback(() => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    const linkedNodeIds = dataSettings.inputLinks.map((link) => link.nodeId)
    return allNodes
      .filter((node: any) => node.id !== id && !linkedNodeIds.includes(node.id))
      .map((node: any) => {
        const nodeData: Record<string, any> = {
          nodeId: { value: node.id, enabled: false, label: 'Node ID' },
          nodeType: { value: node.type || 'unknown', enabled: false, label: 'Node Type' },
        }
        if (node.data?.contentMarkdown && node.data?.dataSettings?.documentSections?.fullDocument?.enabled) {
          nodeData.fullDocument = {
            value: node.data.contentMarkdown,
            enabled: false,
            label: 'Full Document Content',
            description: 'The complete document content',
          }
        }
        if (node.data?.dataSettings?.documentSections?.sections) {
          Object.entries(node.data.dataSettings.documentSections.sections)
            .filter(([_, section]: [string, any]) => section.enabled)
            .forEach(([title, section]: [string, any]) => {
              nodeData[`section:${title}`] = {
                value: section.value || '',
                enabled: false,
                label: `Section: ${title}`,
                description: `Document section: ${title}`,
              }
            })
        }
        if (node.data?.dataSettings?.exposedData) {
          Object.entries(node.data.dataSettings.exposedData)
            .filter(([_, field]: [string, any]) => field.enabled && field.permissions?.readable)
            .forEach(([key, field]: [string, any]) => {
              nodeData[`exposed:${key}`] = {
                value: field.value || '',
                enabled: false,
                label: `${field.label || key} (Exposed)`,
                description: field.description || `Exposed field: ${key}`,
                permissions: field.permissions || { readable: true, writable: false },
              }
            })
        }
        return {
          nodeId: node.id,
          nodeName: node.data?.label || node.id,
          enabled: true,
          nodeType: node.type || 'Unknown',
          data: nodeData,
        }
      })
  }, [id, dataSettings.inputLinks])

  const handleDataSettingsChange = (newSettings: NodeDataSettings) => {
    // Remove: setDataSettings(newSettings) - violates syncNodeData pattern
    if (newSettings.documentSections) {
      setSectionEnabledStates((prev) => ({
        fullDocument: newSettings.documentSections!.fullDocument.enabled,
        sections: Object.fromEntries(
          Object.entries(newSettings.documentSections!.sections).map(([title, section]) => [title, section.enabled])
        ),
      }))
    }
    // Only sync upstream here
    const linkedKnowledgeText = generateLinkedKnowledgeText(newSettings)
    if (linkedKnowledgeText !== data.linkedKnowledge) {
      syncNodeData({ dataSettings: newSettings, linkedKnowledge: linkedKnowledgeText })
    } else {
      syncNodeData({ dataSettings: newSettings })
    }
  }

  const handleRemoveInputLink = useCallback((nodeIdToRemove: string) => {
    // Don't need to manage available nodes anymore since they're computed dynamically
    const updatedInputLinks = dataSettings.inputLinks.filter((link) => link.nodeId !== nodeIdToRemove)
    const newSettings = { ...dataSettings, inputLinks: updatedInputLinks }

    // Only sync upstream - the dialog will recompute available nodes automatically
    syncNodeData({ dataSettings: newSettings })
    setTimeout(() => generateLinkedKnowledgeText(), 0)
  }, [generateLinkedKnowledgeText, syncNodeData, dataSettings])

  const handleSyncInputLink = useCallback(async (linkNodeId: string) => {
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    const linkedNode = allNodes.find((node) => node.id === linkNodeId)
    if (!linkedNode) return { success: false, error: `Node ${linkNodeId} not found` }
    const linkIndex = dataSettings.inputLinks.findIndex((link) => link.nodeId === linkNodeId)
    if (linkIndex === -1) return { success: false, error: `Node ${linkNodeId} not in input links` }
    const nodeData: Record<string, any> = {
      nodeId: { value: linkedNode.id, enabled: true, label: 'Node ID' },
      nodeType: { value: linkedNode.type || 'unknown', enabled: true, label: 'Node Type' },
    }
    if (linkedNode.data?.contentMarkdown) {
      nodeData.fullDocument = {
        value: linkedNode.data.contentMarkdown,
        enabled: true,
        label: 'Full Document Content',
        description: 'The complete document content',
        lastUpdated: Date.now(),
      }
    }
    if (linkedNode.data?.dataSettings?.documentSections?.sections) {
      Object.entries(linkedNode.data.dataSettings.documentSections.sections).forEach(([title, section]: [string, any]) => {
        nodeData[`section:${title}`] = {
          value: section.value || '',
          enabled: section.enabled !== false,
          label: `Section: ${title}`,
          description: `Document section: ${title}`,
          lastUpdated: section.lastUpdated || Date.now(),
        }
      })
    }
    if (linkedNode.data?.dataSettings?.exposedData) {
      Object.entries(linkedNode.data.dataSettings.exposedData).forEach(([key, field]: [string, any]) => {
        if (field.enabled !== false) {
          nodeData[`exposed:${key}`] = {
            value: field.value || '',
            enabled: true,
            label: field.label || `${key} (Exposed)`,
            description: field.description || `Exposed field: ${key}`,
            permissions: field.permissions || { readable: true, writable: false },
            lastUpdated: field.lastUpdated || Date.now(),
          }
        }
      })
    }
    const currentLink = dataSettings.inputLinks[linkIndex]
    Object.keys(currentLink.data).forEach((key) => {
      if (nodeData[key]) nodeData[key].enabled = currentLink.data[key].enabled
    })
    const updatedInputLinks = [...dataSettings.inputLinks]
    updatedInputLinks[linkIndex] = { ...currentLink, data: nodeData, lastSynced: Date.now() }
    const updatedSettings = { ...dataSettings, inputLinks: updatedInputLinks }
    // Remove: setDataSettings(updatedSettings) - violates syncNodeData pattern
    syncNodeData({ dataSettings: updatedSettings })
    generateLinkedKnowledgeText(updatedSettings)
    return { success: true, newData: nodeData }
  }, [dataSettings, syncNodeData, generateLinkedKnowledgeText])

  const handleOpenDataSettingsDialog = () => {
    // Don't compute available nodes here - let the dialog compute them dynamically
    const allNodes = (window as any).__flowGetAllNodes?.() || []
    const outputLinks = allNodes
      .filter((node: any) => node.id !== id && node.data?.dataSettings?.inputLinks?.some((link: any) => link.nodeId === id))
      .map((node: any) => {
        const fields: string[] = []
        const relevantLinks = node.data.dataSettings.inputLinks.filter((link: any) => link.nodeId === id)
        relevantLinks.forEach((link: any) => {
          Object.entries(link.data || {}).forEach(([key, field]: [string, any]) => {
            if (field.enabled) fields.push(key)
          })
        })
        return { nodeId: node.id, nodeName: node.data?.label || node.id, nodeType: node.type, fields }
      })
    const updatedSettings = {
      ...dataSettings,
      documentSections: prepareSectionsForDataSettings(),
      exposedData: prepareExposedDataFields(),
      // Remove availableNodes from persistent storage
      outputLinks,
      autoSync: dataSettings.autoSync !== undefined ? dataSettings.autoSync : false,
    }
    // Remove: setDataSettings(updatedSettings) - violates syncNodeData pattern
    syncNodeData({ dataSettings: updatedSettings })
    setIsDataSettingsDialogVisible(true)
  }

  // Initial setup from data.dataSettings or data.inputdata
  useEffect(() => {
    if (data.dataSettings) {
      //console.log('[useDataSettings] received new dataSettings:', data.dataSettings)
      setDataSettings((prev) => {
        // Prefer non-empty inputLinks from new data, else keep previous
        const mergedInputLinks =
          (data.dataSettings.inputLinks && data.dataSettings.inputLinks.length > 0)
            ? data.dataSettings.inputLinks
            : prev.inputLinks && prev.inputLinks.length > 0
              ? prev.inputLinks
              : []
        return {
          ...prev,
          ...data.dataSettings,
          inputLinks: mergedInputLinks,
        }
      })
      if (data.dataSettings.documentSections) {
        setSectionEnabledStates({
          fullDocument: data.dataSettings.documentSections.fullDocument.enabled,
          sections: Object.fromEntries(
            Object.entries(data.dataSettings.documentSections.sections).map(([title, section]) => [title, section.enabled])
          ),
        })
      }
    } else if (data.inputdata) {
      const inputLinks = Object.entries(data.inputdata).map(([nodeId, fields]) => ({
        nodeId,
        nodeName: nodeId.split('-')[0],
        enabled: true,
        data: fields,
      }))
      setDataSettings((prev) => ({ ...prev, inputLinks }))
    }
  }, [data.dataSettings, data.inputdata])

  // Generate linked knowledge only when inputLinks change
  useEffect(() => {
    const currentInputLinks = dataSettings.inputLinks
    const prevInputLinks = prevInputLinksRef.current
    const linksChanged = JSON.stringify(currentInputLinks) !== JSON.stringify(prevInputLinks)
    if (linksChanged) {
      //console.log('[useDataSettings] inputLinks changed:', currentInputLinks)
      generateLinkedKnowledgeText()
      prevInputLinksRef.current = currentInputLinks.map(link => ({ ...link })) // Deep copy to avoid reference issues
    }
  }, [dataSettings.inputLinks, generateLinkedKnowledgeText])

  // useEffect to sync document sections when contentMarkdown changes
  useEffect(() => {
    if (contentMarkdown === prevContentMarkdownRef.current) {
      return; // No change in content
    }
    prevContentMarkdownRef.current = contentMarkdown; // Update ref after check

    // Assuming extractSectionsFromMarkdown returns Section[] based on linter errors
    const currentSectionsArray: Section[] = extractSectionsFromMarkdown(contentMarkdown);
    const existingDataSettings = dataSettings; // Use a stable reference within the effect
    const existingSettingsSections = existingDataSettings.documentSections?.sections || {};
    const existingFullDocument = existingDataSettings.documentSections?.fullDocument;
    const existingDocumentSectionsEnabled = existingDataSettings.documentSections?.enabled ?? true;

    let sectionsChanged = false;
    const newSettingsSections: Record<string, { value: string; enabled: boolean; lastUpdated: number }> = {};
    const currentSectionTitles = new Set<string>();

    // 1. Iterate through current sections found in markdown (as an array)
    currentSectionsArray.forEach((section) => {
      currentSectionTitles.add(section.title);
      const sectionTitle = section.title;
      const sectionContent = section.content;
      const existingSection = existingSettingsSections[sectionTitle];

      // Determine enabled state: Use local component state first, then existing setting, default true
      const currentEnabledState = sectionEnabledStates.sections[sectionTitle] ?? existingSection?.enabled ?? true;
      const contentValueSame = existingSection?.value === sectionContent;
      const enabledStateSame = existingSection?.enabled === currentEnabledState;

      if (!existingSection || !contentValueSame || !enabledStateSame) {
        sectionsChanged = true; // Mark change if new, content changed, or enabled state changed
        newSettingsSections[sectionTitle] = {
          value: sectionContent,
          enabled: currentEnabledState,
          // Update timestamp only if content value changed
          lastUpdated: !contentValueSame ? Date.now() : (existingSection?.lastUpdated ?? Date.now()),
        };
      } else {
        // No changes in this section, preserve exactly
        newSettingsSections[sectionTitle] = existingSection;
      }
    });

    // 2. Check if any sections were removed (compare titles)
    const removedSections = Object.keys(existingSettingsSections).filter(title => !currentSectionTitles.has(title));
    if (removedSections.length > 0) {
      sectionsChanged = true;
    }

    // 3. Check full document changes (value or enabled state)
    const fullDocumentValueSame = existingFullDocument?.value === contentMarkdown;
    const currentFullDocumentEnabledState = sectionEnabledStates.fullDocument ?? existingFullDocument?.enabled ?? true;
    const fullDocumentEnabledSame = existingFullDocument?.enabled === currentFullDocumentEnabledState;
    const fullDocumentChanged = !fullDocumentValueSame || !fullDocumentEnabledSame;

    // 4. Determine if an update is needed
    if (sectionsChanged || fullDocumentChanged) {
      const newDocumentSections = {
        fullDocument: {
          value: contentMarkdown,
          enabled: currentFullDocumentEnabledState,
          // Update timestamp only if content value changed
          lastUpdated: !fullDocumentValueSame ? Date.now() : (existingFullDocument?.lastUpdated ?? Date.now()),
        },
        sections: newSettingsSections,
        enabled: existingDocumentSectionsEnabled, // Keep the top-level enabled state
      };

      // Final check: Only sync if the generated sections object is different from the existing one
      if (JSON.stringify(existingDataSettings.documentSections) !== JSON.stringify(newDocumentSections)) {
        const newSettings: NodeDataSettings = {
          ...existingDataSettings,
          documentSections: newDocumentSections,
        };

        //setDataSettings(newSettings); // Update local state
        syncNodeData({ dataSettings: newSettings }); // Sync upstream
      }
    }
  }, [contentMarkdown, dataSettings, syncNodeData, sectionEnabledStates]);

  // Keep local dataSettings in sync with data.dataSettings if it changes externally
  useEffect(() => {
    if (data.dataSettings) {
      //console.log('[useDataSettings] received new dataSettings:', data.dataSettings)
      setDataSettings((prev) => ({
        ...prev,
        ...data.dataSettings,
      }))
      if (data.dataSettings.documentSections) {
        setSectionEnabledStates({
          fullDocument: data.dataSettings.documentSections.fullDocument.enabled,
          sections: Object.fromEntries(
            Object.entries(data.dataSettings.documentSections.sections).map(([title, section]) => [title, section.enabled])
          ),
        })
      }
    }
    // No else: don't overwrite if not present
     
  }, [data.dataSettings])

  return {
    dataSettings,
    setDataSettings,
    handleDataSettingsChange,
    handleRemoveInputLink,
    handleSyncInputLink,
    handleOpenDataSettingsDialog,
    generateLinkedKnowledgeText,
    prepareSectionsForDataSettings,
    prepareExposedDataFields,
    fetchAvailableDocumentNodes,
  }
}

export default useDataSettings